import { inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { produce, WritableDraft } from 'immer';
import {
  concatMap,
  finalize,
  first,
  forkJoin,
  from,
  map,
  Observable,
  of,
  switchMap,
  timer,
  toArray,
} from 'rxjs';

import {
  CanvasElementId,
  CanvasElementType,
  getRootElementId,
  SlotId,
} from '@blaze-runtime/engine-domain';
import { syncSelect } from '@blaze-shared/platform-data-access';

import { fromAi, fromElements } from '../../+state';
import { getElementById } from '../../+state/elements';
import { EngineState, getEngineState, selectElementEntities } from '../../+state/engine';
import { ElementDefinitionsService } from '../../services/element-definitions.service';
import {
  BaseAiElementDefinition,
  ComponentWorkflows,
  ElementContext,
  ParentContext,
} from '../models/ai.model';

import { ElementHandler } from './element-handler';
import { ElementHandlerResolver } from './element-handler-resolver.service';
import { IconResolverService } from './icon-resolver.service';
import {
  ComponentDescriptor,
  ComponentWorkflowsRequest,
  PageElement,
  PageLayout,
} from './page-layout.model';
import { createParentContext, findFirstContainerInRoot, resolveComponentWorkflows } from './utils';
import { warnOnUnwiredRelations } from './warn-on-unwired-relations';
import {
  wireActionWorkflowLayoutInputs,
  wireDefaultLayoutInputs,
} from './wire-layout-workflow-inputs';

type AnyElementContext = ElementContext<CanvasElementType>;

interface HandlerResult {
  context: AnyElementContext;
  handler: ElementHandler<CanvasElementType>;
}

interface TraverseContextInfo {
  pageId: string;
  projectId: string;
  idsMapping: Record<string, CanvasElementId>;
  componentWorkflows: ComponentWorkflows;
  datasetNameToId: Record<string, string>;
  componentTemplates: Record<string, string>;
}

interface TraverseArgs {
  element: BaseAiElementDefinition<CanvasElementType>;
  parent: { id: string; index: number; slotId?: SlotId };
  accumulator: AnyElementContext[];
  contextInfo: TraverseContextInfo;
}

const FIRST_CONTAINER_DEFAULT_STYLE = 'Page Title';
const DEFAULT_CONTAINER_STYLE_NAME = 'Content';

@Injectable({ providedIn: 'root' })
export class PageLayoutBuilderService {
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly handlerResolver = inject(ElementHandlerResolver);
  private readonly store = inject(Store);
  private readonly iconResolverService = inject(IconResolverService);

  createPage(page: PageLayout, pageId: string, projectId: string): Observable<void> {
    // Must run before wiring - `processWorkflowInputs` rewrites `Layout#<sourceId>#…` keys to
    // resolved input names.
    warnOnUnwiredRelations(page.page.relations, page.componentWorkflows);

    return this.iconResolverService.waitForIconsToLoad().pipe(
      switchMap(() => {
        const contexts = this.flattenAndCreateContext(page, pageId, projectId);
        return this.prepareHandlers(contexts);
      }),
      map((results) => results.filter((r): r is HandlerResult => !!r)),
      concatMap((results) => this.applyStateChanges(results)),
      switchMap(({ results }) => this.finalizeHandlers(results)),
      map(() => undefined),
    );
  }

  updateElement<T extends CanvasElementType>(
    pageElement: PageElement<T>,
    pageId: string,
    projectId: string,
  ): Observable<void> {
    const { element, workflow } = pageElement;

    return this.iconResolverService.waitForIconsToLoad().pipe(
      switchMap(() => this.store.select(selectElementEntities)),
      first(),
      map((elements) => createParentContext(element.id, elements)),
      switchMap((parentContext) => {
        const contextInfo: TraverseContextInfo = {
          pageId,
          projectId,
          idsMapping: {},
          componentWorkflows: resolveComponentWorkflows(workflow ? { [element.id]: workflow } : {}),
          datasetNameToId: {}, // not supported yet
          componentTemplates: {},
        };
        const context = this.createElementContext(element, parentContext, contextInfo);
        return this.prepareSingleHandler(context);
      }),
      map((result) => {
        if (!result) {
          throw new Error(
            `Cannot update element ${element.id}: no AI element handler for type '${element.$type}'`,
          );
        }
        return result;
      }),
      concatMap((result) => this.applyStateChanges([result])),
      switchMap(({ results }) => this.finalizeHandlers(results)),
      map(() => undefined),
    );
  }

  applyComponents(
    components: ComponentDescriptor[],
    componentWorkflows: ComponentWorkflowsRequest,
    pageId: string,
    projectId: string,
  ): Observable<void> {
    return this.iconResolverService.waitForIconsToLoad().pipe(
      switchMap(() => this.store.select(selectElementEntities).pipe(first())),
      switchMap((elements) => {
        const idsMapping: Record<string, CanvasElementId> = {};
        const resolved = resolveComponentWorkflows(componentWorkflows);
        this.setWorkflowsMetadata(resolved, pageId);

        const parent = findFirstContainerInRoot(elements, pageId);

        const sharedInfo: TraverseContextInfo = {
          pageId,
          projectId,
          idsMapping,
          componentWorkflows: resolved,
          datasetNameToId: {},
          componentTemplates: {},
        };

        const contexts = components.map((component) => {
          const componentWithId = {
            ...component,
            id: component.id ?? crypto.randomUUID(),
          };
          return this.createElementContext(
            componentWithId as BaseAiElementDefinition<CanvasElementType>,
            parent,
            sharedInfo,
          );
        });

        return this.prepareHandlers(contexts).pipe(
          map((results) => results.filter((r): r is HandlerResult => !!r)),
          concatMap((results) => this.applyStateChanges(results)),
          switchMap(({ results }) => this.finalizeHandlers(results)),
          map(() => undefined),
        );
      }),
    );
  }

  private prepareHandlers(contexts: AnyElementContext[]): Observable<(HandlerResult | null)[]> {
    if (!contexts.length) {
      return of([]);
    }

    return forkJoin(contexts.map((ctx) => this.prepareSingleHandler(ctx)));
  }

  private prepareSingleHandler(context: AnyElementContext): Observable<HandlerResult | null> {
    const handler = this.handlerResolver.resolve(context.element.$type);

    if (!handler) {
      return of(null);
    }

    return handler.prepare
      ? handler.prepare(context).pipe(map((resolvedCtx) => ({ context: resolvedCtx, handler })))
      : of({ context, handler });
  }

  private applyStateChanges(results: HandlerResult[]): Observable<{
    results: HandlerResult[];
    state: EngineState;
  }> {
    const currentState = syncSelect(this.store, getEngineState);

    const nextState = produce(currentState, (draft) => {
      for (const { context, handler } of results) {
        handler.process(context, draft);
        this.setSharedProperties(context, draft);
      }
      // Second pass: every element is placed and in `idsMapping` by now, so cross-component
      // `Layout#<sourceId>#<token>` references can resolve regardless of the layout order.
      for (const { context, handler } of results) {
        if (handler.wireCrossComponentInputs) {
          handler.wireCrossComponentInputs(context, draft);
        } else {
          wireDefaultLayoutInputs(draft, context, this.elementDefinitionsService);
        }
        wireActionWorkflowLayoutInputs(draft, context, this.elementDefinitionsService);
      }
    });

    this.store.dispatch(
      fromAi.applyChanges({
        state: {
          ids: nextState.ids,
          entities: nextState.entities,
          connections: nextState.connections,
          workflows: nextState.workflows,
        },
      }),
    );

    return of({ results, state: nextState });
  }

  private finalizeHandlers(results: HandlerResult[]): Observable<unknown[]> {
    const elementIds = results.map(
      ({ context }) => context.idsMapping[context.element.id] ?? context.element.id,
    );

    return from(results).pipe(
      concatMap(({ context, handler }) => {
        if (!handler.finalize) {
          return of(null);
        }

        const finalizeHandler = handler.finalize.bind(handler);
        const elementId = context.idsMapping[context.element.id] ?? context.element.id;
        // `applyChanges` was dispatched synchronously above, but when this builder is
        // invoked from an effect that re-entrant dispatch is queued on NgRx's
        // queueScheduler and only reduced into the store once the triggering action
        // finishes flushing. Defer one macrotask so the new element is committed before
        // getElementById reads it back below (otherwise it's undefined and we throw).
        return timer(1).pipe(
          concatLatestFrom(() => this.store.select(getElementById(elementId))),
          concatMap(([_, element]) => {
            if (!element) {
              throw new Error(
                `Missing element with id ${context.element.id} (mapped: ${elementId})`,
              );
            }

            return finalizeHandler(element, context).pipe(first());
          }),
        );
      }),
      toArray(),
      finalize(() => this.store.dispatch(fromElements.aiGenerationSettled({ elementIds }))),
    );
  }

  private flattenAndCreateContext(
    pageLayout: PageLayout,
    pageId: string,
    projectId: string,
  ): AnyElementContext[] {
    const contexts: AnyElementContext[] = [];
    const rootId = getRootElementId(pageId);

    const componentWorkflows = resolveComponentWorkflows(pageLayout.componentWorkflows ?? {});
    this.setWorkflowsMetadata(componentWorkflows, pageId);

    const contextInfo: TraverseContextInfo = {
      pageId,
      projectId,
      idsMapping: {},
      componentWorkflows,
      datasetNameToId: pageLayout.datasetNameToId ?? {},
      componentTemplates: pageLayout.componentTemplates ?? {},
    };

    pageLayout.page.children.forEach((container, index) => {
      if (!container.styleName) {
        container.styleName ??=
          index === 0 ? FIRST_CONTAINER_DEFAULT_STYLE : DEFAULT_CONTAINER_STYLE_NAME;
      }
      this.traverseElement({
        element: container,
        parent: { id: rootId, index },
        accumulator: contexts,
        contextInfo,
      });
    });

    return contexts;
  }

  private traverseElement(args: TraverseArgs): void {
    const { element, parent, accumulator, contextInfo } = args;
    const definition = this.elementDefinitionsService.getDefinitionByAiNameSafe(element.$type);

    element.id ??= crypto.randomUUID();
    element.$type = definition.type;

    accumulator.push(this.createElementContext(element, parent, contextInfo));

    const handler = this.handlerResolver.resolve(element.$type);
    const childGroups = handler?.getChildGroups?.(element) ?? [];

    for (const group of childGroups) {
      group.children.forEach((child, index) => {
        this.traverseElement({
          ...args,
          element: child,
          parent: { id: element.id, index, slotId: group.slotId },
        });
      });
    }
  }

  private setWorkflowsMetadata(workflows: ComponentWorkflows, pageId: string): void {
    for (const { workflow } of Object.values(workflows)) {
      workflow.pageId ??= pageId;
    }
  }

  private createElementContext(
    element: BaseAiElementDefinition<CanvasElementType>,
    parent: ParentContext,
    info: TraverseContextInfo,
  ): AnyElementContext {
    const definition = this.elementDefinitionsService.getDefinitionSafe(element.$type);
    return {
      element,
      pageId: info.pageId,
      projectId: info.projectId,
      definition,
      parent,
      idsMapping: info.idsMapping,
      componentWorkflows: info.componentWorkflows,
      datasetNameToId: info.datasetNameToId,
      componentTemplates: info.componentTemplates,
    };
  }

  private setSharedProperties(ctx: AnyElementContext, draft: WritableDraft<EngineState>): void {
    const entity = draft.entities[ctx.idsMapping[ctx.element.id]];
    if (entity && ctx.element.aiDescription) {
      entity.description = ctx.element.aiDescription;
    }
  }
}
